import { clsx } from 'clsx';
import { useNotify, useRefresh } from 'react-admin';
import { useEffect, useState } from 'react';

import { useQuery } from '@hooks';
import { rbac } from '@utils';
import { Credit, CreditStatus } from '@types';
import { InitialCard } from '@components';
import { creditFileStatusNames, CreditFileStatusTypes, creditHttp } from '@network';
import cls from './CreditFileStatusWidget.module.css';

interface StepProps {
  title: string;
  status: CreditFileStatusTypes;
  color: string;
  isActive: boolean;
  isClickable: boolean;
}

const Arrow = ({ color }: {color?: string}) => {
  return (
    <svg width="261"
      height="48"
      viewBox="0 0 261 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <g filter="url(#filter0_d_1718_5)">
        <path fill={color || '#D32F2F'}
          d="M256.009 18.2661C257.34 19.0375 257.338 20.9595 256.006 21.7285L224.818 39.7321C224.514 39.9076 224.169 40 223.818 40H4.05139C4.02301 40 4 39.977 4 39.9486C4 39.9303 4.00979 39.9133 4.02569 39.9041L35.5001 21.732C36.8334 20.9622 36.8334 19.0378 35.5001 18.268L4.02569 0.0958883C4.00979 0.0867089 4 0.0697454 4 0.0513868C4 0.0230066 4.02301 0 4.05139 0H223.962C224.314 0 224.661 0.0930446 224.965 0.26972L256.009 18.2661Z" />
      </g>
      <defs>
        <filter id="filter0_d_1718_5"
          x="0"
          y="0"
          width="261.006"
          height="48"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha" />
          <feOffset dy="4" />
          <feGaussianBlur stdDeviation="2" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1718_5" />
          <feBlend mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_1718_5"
            result="shape" />
        </filter>
      </defs>
    </svg>
  );
};

export const CreditFileStatusWidget = ({ credit }: {credit: Credit}) => {
  const [steps, setSteps] = useState<StepProps[]>([]);
  const notify = useNotify();
  const refresh = useRefresh();
  const query = useQuery();
  const isScreening = query.get('screening');

  const sendFileStatus = async (status: CreditFileStatusTypes) => {
    await creditHttp.setFileStatus(credit.id, status);
    notify(`The File Status is set as ${creditFileStatusNames[status]}`);
    refresh();
  };

  useEffect(() => {
    if (isScreening) {
      document.getElementById('screening')?.scrollIntoView({
        behavior: 'smooth', block: 'start',
      });
    }
  }, [isScreening]);

  const isCompleted = ![
    CreditStatus.MODERATION,
    CreditStatus.PRE_SCREENING,
    CreditStatus.DOC_COLLECTION,
    CreditStatus.KYC_SCREENING,
    CreditStatus.POST_SCREENING,
    CreditStatus.DRAFT,
    CreditStatus.DENIED,
  ].includes(credit.status);

  useEffect(() => {
    setSteps([
      {
        title: creditFileStatusNames[CreditFileStatusTypes.PRE_SCREENING],
        status: CreditFileStatusTypes.PRE_SCREENING,
        color: '#1D1DB2',
        isActive: isCompleted
          || credit.status === CreditStatus.PRE_SCREENING
          || credit.status === CreditStatus.DOC_COLLECTION
          || credit.status === CreditStatus.KYC_SCREENING
          || credit.status === CreditStatus.POST_SCREENING,
        isClickable: false,
      },
      {
        title: creditFileStatusNames[CreditFileStatusTypes.DOC_COLLECTION],
        status: CreditFileStatusTypes.DOC_COLLECTION,
        color: '#3B3BB8',
        isActive: isCompleted
          || credit.status === CreditStatus.DOC_COLLECTION
          || credit.status === CreditStatus.KYC_SCREENING
          || credit.status === CreditStatus.POST_SCREENING,
        isClickable: false,
      },
      {
        title: creditFileStatusNames[CreditFileStatusTypes.KYC_SCREENING],
        status: CreditFileStatusTypes.KYC_SCREENING,
        color: '#007BFF',
        isActive: isCompleted
          || credit.status === CreditStatus.KYC_SCREENING
          || credit.status === CreditStatus.POST_SCREENING,
        isClickable: false,
      },
      {
        title: creditFileStatusNames[CreditFileStatusTypes.POST_SCREENING],
        status: CreditFileStatusTypes.POST_SCREENING,
        color: '#00BFFF',
        isActive: isCompleted
          || credit.status === CreditStatus.POST_SCREENING,
        isClickable: false,
      },
    ]);
  }, [credit.status]);

  return (
    <InitialCard
      left="Status Progress"
      wrapperClass={clsx(cls.cardWrapper, isScreening && cls.isScreening)}
      contentClass={cls.cardContent}
      minHeight={70}
    >
      <div className={cls.wrap} id="screening">
        {steps.map((step, index) => (
          <div
            key={index}
            className={clsx(
              cls.step,
              step.isActive && cls.stepActive,
              step.isClickable && cls.stepClickable,
            )}
            onClick={step.isClickable ? () => sendFileStatus(step.status) : undefined}
          >
            <div className={cls.stepText}>{step.title}</div>
            <Arrow color={step.color} />
          </div>
        ))}
      </div>
    </InitialCard>
  );
};
